import { useEffect, useMemo, useState } from "react";
import {
  LabelledCheckbox,
  Modal,
  ModalProps,
  Stack,
  Title,
  useToast
} from "@introist/react-foundation/v2";

import { api } from "services/rpc/RpcProvider";
import { ListView } from "foundation";
import { SearchInput } from "components/molecules";
import { ListRow } from "components/organisms";
import { FormFooter } from "modules/datasources/components/FormFooter";
import { AvatarBackground } from "modules/settings/components/AvatarBackground";

type WorkflowAccessModalProps = ModalProps & {
  resourceId: string;
  resourceType: "folder" | "workflow";
  resourceName: string;
};

export const WorkflowAccessModal = ({
  resourceId,
  resourceType,
  resourceName,
  ...props
}: WorkflowAccessModalProps) => {
  return (
    <Modal title={`Manage access to ${resourceName}`} style={{ width: 540 }} {...props}>
      {props.open && (
        <WorkflowAccessForm
          resourceId={resourceId}
          resourceType={resourceType}
          onDone={() => props.onClose && props.onClose()}
        />
      )}
    </Modal>
  );
};

const WorkflowAccessForm = ({
  resourceId,
  resourceType,
  onDone
}: {
  resourceId: string;
  resourceType: "folder" | "workflow";
  onDone: () => void;
}) => {
  const toast = useToast();
  const apiUtils = api.useContext();

  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  const { data: allUsers } = api.users.list.useQuery({});

  const grantAccess = api.workflows.v4.access.grant.useMutation();
  const revokeAccess = api.workflows.v4.access.revoke.useMutation();

  // Fetch grants for all users
  const userGrantQueries = api.useQueries(t =>
    (allUsers ?? []).map(user => t.workflows.v4.access.list({ userId: user.userId }))
  );

  // Filter grants for this specific resource
  const resourceGrants = useMemo(() => {
    const grants: Array<{ id: string; userId: string }> = [];
    userGrantQueries.forEach((query, index) => {
      if (query.data && allUsers) {
        const user = allUsers[index];
        query.data.forEach(grant => {
          const isMatch =
            resourceType === "folder" ? grant.folderId === resourceId : grant.workflowId === resourceId;
          if (isMatch) {
            grants.push({ id: grant.id, userId: user.userId });
          }
        });
      }
    });
    return grants;
  }, [userGrantQueries, allUsers, resourceId, resourceType]);

  // Load initial selected users from grants
  useEffect(() => {
    if (resourceGrants.length > 0) {
      setSelectedUserIds(new Set(resourceGrants.map(g => g.userId)));
    }
    // Only seed selection from the initial load, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGrantQueries.some(q => q.isSuccess)]);

  // Filter users for search and exclude admins/owners (they have full access)
  const filteredUsers = useMemo(() => {
    if (!allUsers) return [];
    // Only show regular users - admins and owners have full access by default
    const regularUsers = allUsers.filter(user => user.role === "user");
    if (!search) return regularUsers;
    const term = search.toLowerCase();
    return regularUsers.filter(user => user.email.toLowerCase().includes(term));
  }, [allUsers, search]);

  const toggleUser = (userId: string, checked: boolean) => {
    setSelectedUserIds(current => {
      const next = new Set(current);
      if (checked) next.add(userId);
      else next.delete(userId);
      return next;
    });
  };

  const onSubmit = async () => {
    const currentUserIds = new Set(resourceGrants.map(g => g.userId));

    const toGrant = Array.from(selectedUserIds).filter(userId => !currentUserIds.has(userId));
    const toRevoke = resourceGrants.filter(g => !selectedUserIds.has(g.userId));

    const grantParams =
      resourceType === "folder" ? { folderId: resourceId } : { workflowId: resourceId };

    await Promise.all([
      ...toGrant.map(userId => grantAccess.mutateAsync({ userId, ...grantParams })),
      ...toRevoke.map(g => revokeAccess.mutateAsync({ id: g.id }))
    ]);

    // Invalidate all user grant queries
    await Promise.all(
      (allUsers ?? []).map(user =>
        apiUtils.workflows.v4.access.list.invalidate({ userId: user.userId })
      )
    );

    toast.success("Access updated");
    onDone();
  };

  return (
    <Stack vertical gap="medium">
      <Stack vertical gap="small">
        <Title variant="bold">Who has access</Title>
        <Title small color="var(--palette-foreground-subdued)">
          Select which users can access this {resourceType}
        </Title>
      </Stack>

      <SearchInput
        value={search}
        onChange={value => setSearch(value ?? "")}
        placeholder="Search users by name or email..."
      />

      <ListView.List
        data={filteredUsers}
        style={{ maxHeight: 360, overflow: "auto" }}
        empty={{ title: "No users found" }}
        renderRow={user => (
          <ListRow key={user.userId} gridTemplateColumns="auto 1fr">
            <LabelledCheckbox
              label=""
              checked={selectedUserIds.has(user.userId)}
              onChange={checked => toggleUser(user.userId, checked)}
            />
            <Stack gap="small" alignItems="center">
              <AvatarBackground>
                <Title variant="bold">{user.email[0].toUpperCase()}</Title>
              </AvatarBackground>
              <Title variant="bold">{user.email}</Title>
            </Stack>
          </ListRow>
        )}
      />

      <FormFooter onCancel={onDone} onSubmit={onSubmit} />
    </Stack>
  );
};
